import type { InventoryItem } from '../types/inventory.types';
import { InventoryService } from '../services';

interface UseInventoryItemActionsProps {
  companyId: string | null;
  inventoryItems: InventoryItem[];
  showSnackbar: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
  setInventoryItems: React.Dispatch<React.SetStateAction<InventoryItem[]>>;
  handleRefreshInventory: () => Promise<void>;
}

export const useInventoryItemActions = ({
  companyId,
  inventoryItems,
  showSnackbar,
  setInventoryItems,
  handleRefreshInventory,
}: UseInventoryItemActionsProps) => {
  
  // QR Menü durumunu değiştir (Aç/Kapat)
  const toggleQRMenu = async (menuItemId: string) => {
    if (!menuItemId || !companyId) {
      showSnackbar('Ürün bilgileri eksik', 'error');
      return;
    }

    const item = inventoryItems.find(item => item.id === menuItemId);
    if (!item) {
      showSnackbar('Ürün bulunamadı', 'error');
      return;
    }

    // Şu anki durumun tersini yapacağız (toggle)
    const currentStatus = item.isQRMenu || false;
    const newStatus = !currentStatus;

    try {
      showSnackbar(
        newStatus 
          ? 'Ürün QR Menü\'de açılıyor...' 
          : 'Ürün QR Menü\'den kapatılıyor...', 
        'info'
      );
      
      // InventoryService kullanarak toggle et (SQL API V2)
      const result = await InventoryService.toggleQRMenuStatus(companyId, menuItemId, newStatus);
      
      if (!result.success) {
        throw new Error(result.message || 'QR Menü durumu değiştirilemedi');
      }
      
      // ✅ State'de isQRMenu durumunu güncelle (UI'da anında görünsün)
      setInventoryItems(prevItems =>
        prevItems.map(i =>
          i.id === menuItemId
            ? { 
                ...i, 
                isQRMenu: newStatus,
                qrMenuSentAt: newStatus ? new Date().toISOString() : undefined,
                qrMenuRemovedAt: newStatus ? undefined : new Date().toISOString()
              }
            : i
        )
      );
      
      showSnackbar(
        `${item.name} başarıyla QR Menü'de ${newStatus ? 'açıldı' : 'kapatıldı'}!`, 
        'success'
      );
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      console.error('❌ [QR MENU TOGGLE] Hata:', error);
      showSnackbar(error instanceof Error ? error.message : 'QR Menü durumu değiştirilemedi', 'error');
    }
  };

  // Kiosk durumunu değiştir (Aç/Kapat)
  const toggleKiosk = async (menuItemId: string) => {
    if (!menuItemId || !companyId) {
      showSnackbar('Ürün bilgileri eksik', 'error');
      return;
    }

    const item = inventoryItems.find(item => item.id === menuItemId);
    if (!item) {
      showSnackbar('Ürün bulunamadı', 'error');
      return;
    }

    // Şu anki durumun tersini yapacağız (toggle)
    const currentStatus = item.isKiosk || false;
    const newStatus = !currentStatus;

    try {
      showSnackbar(
        newStatus 
          ? 'Ürün Kiosk\'ta açılıyor...' 
          : 'Ürün Kiosk\'tan kapatılıyor...', 
        'info'
      );
      
      // InventoryService kullanarak toggle et (SQL API V2)
      const result = await InventoryService.toggleKioskStatus(companyId, menuItemId, newStatus);
      
      if (!result.success) {
        throw new Error(result.message || 'Kiosk durumu değiştirilemedi');
      }
      
      // ✅ State'de isKiosk durumunu güncelle (UI'da anında görünsün)
      setInventoryItems(prevItems =>
        prevItems.map(i =>
          i.id === menuItemId
            ? { 
                ...i, 
                isKiosk: newStatus,
                kioskSentAt: newStatus ? new Date().toISOString() : undefined,
                kioskRemovedAt: newStatus ? undefined : new Date().toISOString()
              }
            : i
        )
      );
      
      showSnackbar(
        `${item.name} başarıyla Kiosk'ta ${newStatus ? 'açıldı' : 'kapatıldı'}!`, 
        'success'
      );
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      console.error('❌ [KIOSK TOGGLE] Hata:', error);
      showSnackbar(error instanceof Error ? error.message : 'Kiosk durumu değiştirilemedi', 'error');
    }
  };

  // Pasif ürünü aktifleştir
  const activateItem = async (menuItemId: string) => {
    if (!menuItemId || !companyId) {
      showSnackbar('Ürün bilgileri eksik', 'error');
      return;
    }

    try {
      console.log('🔄 [ACTIVATE ITEM] Ürün aktifleştiriliyor:', menuItemId);
      
      // SQL API ile aktifleştir
      await InventoryService.bulkUpdateStatus(companyId, [menuItemId], true); // ✅ companyId eklendi
      
      // State'i güncelle
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          item.id === menuItemId ? { ...item, isActive: true } : item
        )
      );
      
      const item = inventoryItems.find(item => item.id === menuItemId);
      showSnackbar(`${item?.name || 'Ürün'} başarıyla aktifleştirildi`, 'success');
      console.log('✅ [ACTIVATE ITEM] Ürün aktifleştirildi');
      
    } catch (error) {
      console.error('❌ [ACTIVATE ITEM] Aktifleştirme hatası:', error);
      showSnackbar('Ürün aktifleştirilirken bir hata oluştu', 'error');
    }
  };

  return {
    toggleQRMenu,
    toggleKiosk,
    activateItem,
  };
};

